'use client';
import React from 'react';
import { Search, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { FilterState } from './DiscoverContent';

const categoryTabs = ['All', 'Knowledge', 'Coding', 'Creative', 'Games', 'Mathematics', 'Marketing', 'Tournaments', 'Fitness'];

const sortOptions = [
  { value: 'trending', label: 'Trending' },
  { value: 'newest', label: 'Newest' },
  { value: 'prize-high', label: 'Highest Prize' },
  { value: 'ending-soon', label: 'Ending Soon' },
  { value: 'most-players', label: 'Most Players' },
  { value: 'entry-low', label: 'Lowest Entry' },
];

interface DiscoverHeaderProps {
  filters: FilterState;
  onUpdateFilter: (key: keyof FilterState, value: string | number) => void;
  filtersOpen: boolean;
  onToggleFilters: () => void;
}

export default function DiscoverHeader({ filters, onUpdateFilter, filtersOpen, onToggleFilters }: DiscoverHeaderProps) {
  return (
    <div className="space-y-4">
      {/* Title + search row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Discover Activities</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
            Showing 500+ competitions, quizzes, games and challenges
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sort */}
          <div className="relative">
            <select
              value={filters.sortBy}
              onChange={(e) => onUpdateFilter('sortBy', e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 text-sm rounded-lg outline-none"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
            >
              {sortOptions.map((opt) => (
                <option key={`sort-${opt.value}`} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
          </div>

          {/* Filter toggle */}
          <button
            onClick={onToggleFilters}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              background: filtersOpen ? 'rgba(124,58,237,0.15)' : 'var(--muted)',
              border: `1px solid ${filtersOpen ? 'rgba(124,58,237,0.4)' : 'var(--border)'}`,
              color: filtersOpen ? '#A78BFA' : 'var(--muted-foreground)',
            }}
          >
            <SlidersHorizontal size={14} />
            Filters
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
        <input
          type="text"
          placeholder="Search activities, quizzes, challenges, tournaments..."
          value={filters.search}
          onChange={(e) => onUpdateFilter('search', e.target.value)}
          className="w-full pl-11 pr-4 py-3 text-sm rounded-xl outline-none transition-all"
          style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            color: 'var(--foreground)',
          }}
          onFocus={(e) => { e.target.style.borderColor = 'var(--primary)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; }}
        />
      </div>

      {/* Category tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
        {categoryTabs.map((tab) => (
          <button
            key={`cat-tab-${tab}`}
            onClick={() => onUpdateFilter('category', tab)}
            className={`tab-pill flex-shrink-0 ${filters.category === tab ? 'active' : ''}`}
          >
            {tab}
          </button>
        ))}
      </div>
    </div>
  );
}